import { uploadFileApi } from "@/api/services/global/file";
import { IconClose } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { FileItem } from "@/components/elements/FileItem/FileItem";
import { useFormField } from "@/components/elements/FormField";
import { YEAR_MONTH_DAY_STR_FORMAT } from "@/constants/datetime";
import { ACCEPT_ERROR, FILE_STATUS, TFileUploadFor, MAX_SIZE_ERROR } from "@/constants/file";
import { button, divider, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { FCC } from "@/types/common";
import { TExtendFile, TExtendFileOption } from "@/types/file";
import { dayjs } from "@/utils/dayjs";
import { showError } from "@/utils/error";
import {
  TFileRules,
  checkFileListWhetherIsActualPdf,
  convertMbToByte,
  getExtensionsOfMimeTypes,
  getFileErrorMessage,
  getFileStatusWithRules,
} from "@/utils/file";
import { makeUuid } from "@/utils/pieces";
import { truncateFileNameMiddle } from "@/utils/string";
import { toast } from "@/utils/toast";
import { Box, Grid, IconButton, Stack, Typography, css, styled } from "@mui/material";
import { Fragment, Key, useCallback, useEffect, useRef, useState } from "react";
import { DropzoneOptions, FileRejection, useDropzone } from "react-dropzone";

export type TTalentDropZoneProps = {
  ref?: React.MutableRefObject<HTMLDivElement | undefined>;
  value?: TExtendFile | TExtendFile[] | null;
  onChange?: (value: TExtendFile | TExtendFile[] | null) => void;
  options?: Omit<DropzoneOptions, "onDrop">;
  enableFileList?: boolean;
  enableFileListCustom?: boolean;
  enableTextFileList?: boolean;
  nonControl?: boolean;
  rules?: TFileRules;
  bordered?: boolean;
  disabled?: boolean;
  uploadFor?: TFileUploadFor;
  hiddenCloseIcon?: boolean;
  hideAfterUpload?: boolean;
  handleFileRejections?: (fileRejections: FileRejection[]) => void;
  childrenCustom?: React.ReactNode | ((index: number) => React.ReactNode);
  // Optional per-file footer content rendered below the filename row inside FileItem.
  // Used for per-file async status UI like OCR warning spinner / message.
  footerCustom?: (index: number) => React.ReactNode;
  onFileRemove?: (removedIndex: number, updatedFiles: TExtendFile[]) => void;
  triggerOpenChosenFile?: (fn: () => void) => void;
  validateBeforeAdd?: (newFilesCount: number, currentFilesCount: number) => string | undefined;
  isEditMode?: boolean;
  twoColumnLayout?: boolean;
};

export const TalentDropZone: FCC<TTalentDropZoneProps> = ({
  options = {},
  value,
  onChange,
  enableFileList = true,
  enableFileListCustom = false,
  enableTextFileList = false,
  children,
  rules = {},
  nonControl = false,
  bordered = true,
  hiddenCloseIcon = false,
  disabled,
  uploadFor,
  hideAfterUpload = false,
  childrenCustom,
  footerCustom,
  handleFileRejections,
  onFileRemove,
  triggerOpenChosenFile,
  validateBeforeAdd,
  isEditMode = false,
  twoColumnLayout = false,
}) => {
  const { field } = useFormField(nonControl);
  const [files, setFiles] = useState<TExtendFile[]>([field?.value ?? value ?? []].flat());
  const filesRef = useRef(files);
  const [uploading, setUploading] = useState(false);
  const [hideDropZone, setHideDropZone] = useState(false);

  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  useEffect(() => {
    const incomingFiles: TExtendFile[] = [field?.value ?? value ?? []].flat();

    const areDifferent =
      incomingFiles.length !== files.length ||
      incomingFiles.some((newFile) => {
        const found = files.find((oldFile) => {
          if (newFile.id && oldFile.id) {
            return newFile.id === oldFile.id;
          }
          if (newFile.filePath && oldFile.filePath) {
            return newFile.filePath === oldFile.filePath;
          }
          if (newFile instanceof File && oldFile instanceof File) {
            return newFile.name === oldFile.name && newFile.size === oldFile.size && newFile.lastModified === oldFile.lastModified;
          }
          return false;
        });
        return !found;
      });

    if (areDifferent) {
      setFiles(incomingFiles);
    }
  }, [value, field?.value, files]);

  useEffect(() => {
    if (hideAfterUpload && files.length > 0) {
      setHideDropZone(true);
      return;
    }

    setHideDropZone(false);
  }, [files]);

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0 && handleFileRejections) {
        handleFileRejections(fileRejections);
        return;
      }

      if (fileRejections.length > 0 && rules.acceptFileTypesExtension) {
        const extensions = getExtensionsOfMimeTypes(rules?.acceptFileTypesExtension || []);
        toast.error(ACCEPT_ERROR(extensions));
        return;
      }

      const currentFiles = filesRef.current;
      if (rules?.maxFile && acceptedFiles.length + currentFiles.length > rules?.maxFile) {
        toast.error(`ファイルは最大${rules?.maxFile}件までアップロードできます。`);
        return;
      }

      if (validateBeforeAdd) {
        const errorMessage = validateBeforeAdd(acceptedFiles.length, currentFiles.length);
        if (errorMessage) {
          toast.error(errorMessage);
          return;
        }
      }

      const invalidPdf = await checkFileListWhetherIsActualPdf(acceptedFiles);
      if (invalidPdf) {
        toast.error("アップロードされたファイルが正しいPDF形式でないため、アップロードできません。");
        return;
      }

      const maxSize = rules?.maxSize ?? convertMbToByte(10);

      for (const file of acceptedFiles) {
        const isValidSize = file.size <= maxSize;

        if (!isValidSize) {
          toast.error(MAX_SIZE_ERROR(maxSize));
          return;
        }
      }
      setUploading(true);
      const droppedFiles = await Promise.all(
        acceptedFiles.map(async (file) => {
          const fileOption: TExtendFileOption = {
            id: makeUuid(),
            status: getFileStatusWithRules(file, rules),
          };

          if (fileOption.status === FILE_STATUS.OK && uploadFor) {
            try {
              const rs = await uploadFileApi({ file, uploadFor });
              return Object.assign(file, { ...fileOption, filePath: rs.data.filePath });
            } catch (error) {
              showError(error);
              return Object.assign(file, { ...fileOption, status: FILE_STATUS.ERROR });
            }
          }
          return Object.assign(file, fileOption);
        }),
      );

      setFiles((currentFiles) => {
        let newFiles: TExtendFile[];
        if (options.multiple) {
          newFiles = [...currentFiles, ...droppedFiles];
        } else {
          newFiles = droppedFiles.length > 0 ? [droppedFiles[0]] : currentFiles;
        }
        triggerChangeFiles(newFiles);
        return newFiles;
      });
      setUploading(false);
    },
    [uploadFor, options.multiple, rules, handleFileRejections],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    ...options,
    onDrop,
    accept: {
      "": rules.acceptFileTypesExtension || [],
    },
    maxSize: options.maxSize || rules.maxSize,
    disabled: uploading || disabled,
    noClick: true,
    noKeyboard: true,
  });

  useEffect(() => {
    triggerOpenChosenFile?.(() => open);
  }, [open]);

  const removeFile = (removedId: Key) => {
    const removedIndex = files.findIndex(({ id }) => id === removedId);
    const updatedFiles = files.filter(({ id }) => removedId !== id);
    setFiles(updatedFiles);
    triggerChangeFiles(updatedFiles);

    if (onFileRemove && removedIndex !== -1) {
      onFileRemove(removedIndex, updatedFiles);
    }
  };

  const triggerChangeFiles = (files: TExtendFile[]) => {
    const { multiple } = options;
    const valueToSet = multiple === false ? (files.at(0) ?? null) : files;
    onChange?.(valueToSet);
    field?.onChange(valueToSet);
    field?.onBlur();
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    open();
  };

  return (
    <Box>
      <Box {...getRootProps()}>
        <input {...getInputProps()} />
        {!hideDropZone && (
          <DropZoneArea dragged={isDragActive} bordered={bordered}>
            {children ?? (
              <>
                <Stack alignItems="center" textAlign="center" gap={"12px"}>
                  <Stack alignItems="center" gap={"2px"}>
                    <Typography variant="body14" color={text.secondary}>
                      アップロードするファイルをドラッグ&ドロップ
                    </Typography>
                    <Typography variant="body14" color={text.tertiary}>
                      pdf、doc、docx、xls、xlsx、ppt、pptx、txt、jpg、jpeg、png、gif、zip、rar、csv
                    </Typography>
                  </Stack>
                  <Typography variant="body14" color={text.secondary}>
                    または
                  </Typography>
                </Stack>

                <Button variant="outline" size="sm" onClick={handleButtonClick}>
                  ファイルを選択する
                </Button>
                <Stack component="ul" sx={{ mt: 1, mb: 0, pl: 2.5, "& li::marker": { fontSize: "0.6em" } }}>
                  <Typography component="li" variant="cap12" color={text.secondary}>
                    最大10ファイルまでアップロードできます。
                  </Typography>
                  <Typography component="li" variant="cap12" color={text.secondary}>
                    容量は最大10MBまでとなります。
                  </Typography>
                </Stack>
              </>
            )}
            {uploading && <UploadingBackup>作成中…</UploadingBackup>}
          </DropZoneArea>
        )}
      </Box>
      {enableFileList && (
        <Stack flexDirection="row" flexWrap="wrap" gap={1} mt={files.length && !hideDropZone ? 1 : 0}>
          {files.map((file) => {
            const fileProps = file.filePath ? { filePath: file.filePath } : { file };
            return (
              <FileItem
                key={file.id}
                {...fileProps}
                downloadable={true}
                onRemove={!hiddenCloseIcon ? () => removeFile(file.id) : undefined}
                maxWidth={336}
                errorMsg={getFileErrorMessage(file, rules)}
              />
            );
          })}
        </Stack>
      )}

      {enableFileListCustom && (
        <FileListWrap mt={files.length && !hideDropZone ? 1 : 0} twoColumnLayout={twoColumnLayout}>
          {files.map((file, index) => {
            const fileProps = file.filePath ? { filePath: file.filePath } : { file };

            const fileItem = (
              <FileItem
                {...fileProps}
                downloadable={true}
                onRemove={!twoColumnLayout && !hiddenCloseIcon ? () => removeFile(file.id) : undefined}
                errorMsg={getFileErrorMessage(file, rules)}
                // twoColumnLayout renders the footer outside FileItem (full-width row below the file row)
                // so the filename column stays single-line and aligned with the tag row.
                footer={twoColumnLayout ? undefined : footerCustom?.(index)}
                updatedAt={file.updatedAt}
              >
                {!twoColumnLayout && (typeof childrenCustom === "function" ? childrenCustom(index) : childrenCustom)}
              </FileItem>
            );

            if (twoColumnLayout) {
              // Cells are direct grid children (no row wrapper) so the file column width is shared across rows.
              return (
                <Fragment key={file.id}>
                  <Box sx={{ minWidth: 0, mt: 2 }}>{fileItem}</Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "flex-end", minWidth: 0, mt: 2 }}>
                    {typeof childrenCustom === "function" ? childrenCustom(index) : childrenCustom}
                    {!hiddenCloseIcon && (
                      <IconButton onClick={() => removeFile(file.id)} sx={{ p: 0.5 }}>
                        <IconClose />
                      </IconButton>
                    )}
                  </Box>
                  {/* Per-file footer (e.g. type-missing error / OCR warning) on its own row below the file row,
                      spanning both columns and sized to fit its content instead of stretching full-width. */}
                  {footerCustom && <Box sx={{ gridColumn: "1 / -1", width: "fit-content" }}>{footerCustom(index)}</Box>}
                </Fragment>
              );
            }

            return (
              <Box
                key={file.id}
                sx={{
                  display: "grid",
                  alignItems: "center",
                  gap: 3,
                  mt: 2,
                }}
              >
                {fileItem}
              </Box>
            );
          })}
        </FileListWrap>
      )}

      {enableTextFileList && (
        <Stack mt={1}>
          {files.map((file, index) => {
            return (
              <Stack mt={1} key={file.id}>
                <Grid container spacing={1} alignItems="flex-start">
                  <Grid item xs={4}>
                    <Typography component="p" variant="cap12">
                      アップロードしたファイル名
                    </Typography>
                    <Typography component="p" variant="body14" mt={1} title={file.name}>
                      {truncateFileNameMiddle(file.name, 37)}
                    </Typography>
                  </Grid>
                  <Grid item xs={7.5}>
                    {typeof childrenCustom === "function" ? childrenCustom(index) : childrenCustom}
                  </Grid>
                  <Grid item xs={0.5} mt={3}>
                    <IconButton
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        removeFile(file.id);
                      }}
                    >
                      <IconClose fontSize="20px" />
                    </IconButton>
                  </Grid>
                </Grid>
              </Stack>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

const options = { shouldForwardProp: (propName: string) => !["dragged", "bordered", "twoColumnLayout"].includes(propName) };

// Shared-width file column: grid sizes column 1 to the LONGEST filename across all rows
// (max-content), so every row's tag column starts at the same x. fit-content() clamps it
// to always reserve ~220px for the tag row (≈1-2 tags + "+N" badge + add button), keeping
// the DEV-282 rule that long names ellipsize instead of stealing tag space. Column width
// depends only on filenames — not on how many tags are VISIBLE — so DocumentTypeTagsRow's
// overflow measurement stays loop-free.
const FileListWrapTwoColumn = css`
  display: grid;
  grid-template-columns: fit-content(calc(100% - 220px)) 1fr;
  column-gap: 4px;
  align-items: center;
`;

const FileListWrap = styled(Stack, options)<{ twoColumnLayout: boolean }>`
  ${({ twoColumnLayout }) => twoColumnLayout && FileListWrapTwoColumn}
`;

const DropZoneAreaActive = css`
  border-color: ${button.primary};
`;

const DropZoneArea = styled(Stack, options)<{ dragged: boolean; bordered: boolean }>`
  position: relative;
  align-items: center;
  justify-content: center;
  padding: 24px;
  gap: 16px;
  border: 1px dashed ${divider.middle};
  border-radius: ${rounded.sm};
  overflow: hidden;

  ${({ dragged }) => dragged && DropZoneAreaActive}
  ${({ bordered }) =>
    !bordered &&
    css`
      padding: 0;
      border: none;
    `}
`;

const UploadingBackup = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  background: ${button.disable};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${text.white};
`;
